import React, { useState } from "react";
import {  X, Save, Loader2 } from "lucide-react";
import { Concurrent } from "../../../../types/features/administration/Concurrent";

// Modal d'édition / création
export const EditConcurrentModal: React.FC<{
  concurrent: Concurrent | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (concurrent: Concurrent) => void;
  isLoading: boolean;
  isCreating: boolean;
}> = ({ concurrent, isOpen, onClose, onSave, isLoading, isCreating }) => {
  const [form, setForm] = useState({ name: "", taxe: "" });
  const [errors, setErrors] = useState<{ name?: string; taxe?: string }>({});

  React.useEffect(() => {
    if (isCreating) {
      setForm({ name: "", taxe: "" });
    } else if (concurrent) {
      setForm({ name: concurrent.name, taxe: concurrent.taxe });
    }
    setErrors({});
  }, [concurrent, isCreating, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: { name?: string; taxe?: string } = {};
    if (!form.name.trim()) {
      newErrors.name = "Le nom est requis";
    }
    if (!form.taxe.trim()) {
      newErrors.taxe = "La taxe est requise";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const data: Concurrent = isCreating
        ? { name: form.name, taxe: form.taxe }
        : { id: concurrent?.id, name: form.name, taxe: form.taxe };
      onSave(data);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-800">
            {isCreating ? "Ajouter un Concurrent" : "Modifier le Concurrent"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom du Concurrent *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, name: e.target.value }));
                if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                errors.name ? "border-red-300" : "border-gray-300"
              }`}
              placeholder="Ex: Western Union, MoneyGram..."
              disabled={isLoading}
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Taxe *</label>
            <input
              type="text"
              value={form.taxe}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, taxe: e.target.value }));
                if (errors.taxe) setErrors((prev) => ({ ...prev, taxe: undefined }));
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                errors.taxe ? "border-red-300" : "border-gray-300"
              }`}
              placeholder="Ex: 5%, 10 CAD..."
              disabled={isLoading}
            />
            {errors.taxe && <p className="mt-1 text-sm text-red-600">{errors.taxe}</p>}
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{isCreating ? "Création..." : "Modification..."}</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>{isCreating ? "Créer" : "Modifier"}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
